import { useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from '@store/hooks';
import {
  createDraft,
  updateStepData,
  commitStep,
  selectSpace as selectSpaceAction,
  goToStep,
  saveAsDraft,
  discardDraft,
  submitForm,
  setEventId,
  setEtag,
  setRequestId,
  setAvailableSpaces,
  setSpacesLoading,
  setApiError,
  setSpacesContinuationToken,
  setSpacesHasMore,
  appendAvailableSpaces,
  setSpacesLoadingMore,
  setSpacesBatchError,
  resetSpacesPagination,
  setSubmissionStatus,
  setValidationErrors,
  clearValidationErrors,
  selectFormData,
  selectCurrentStepIndex,
  selectActiveDraft,
  selectHighestVisitedStep,
  selectSelectedSpace,
  selectAvailableSpaces,
  selectSpacesLoading,
  selectSpacesLoadingMore,
  selectSpacesHasMore,
  selectSpacesBatchError,
  selectSpacesContinuationToken,
  selectSubmissionStatus,
  selectApiError,
  selectEventId,
  selectEtag,
  selectRequestId,
  selectStepValidationErrors,
  selectHasUnsavedChanges,
} from '@store/slices/bookingFormSlice';
import {
  useCreateEventMutation,
  usePatchEventMutation,
  useSubmitEventMutation,
  useDeleteDraftEventMutation,
} from '@store/api/eventService/eventServiceApi';
import { eventServiceApi } from '@store/api/eventService/eventServiceApi';
import {
  useGetBuildingsQuery,
  useGetBuildingByIdQuery,
  useGetRegionConfigQuery,
} from '@store/api/spaceConfigService/spaceConfigServiceApi';
import { spaceConfigServiceApi } from '@store/api/spaceConfigService/spaceConfigServiceApi';
import {
  bookingFormConfig,
  DEFAULT_POLICIES_URL,
  VISIT_PURPOSE_OPTIONS,
} from '@config/bookingFormConfig';
import { DEFAULT_DATE_FORMAT_REGION } from '@config/dateFormatConfig';
import { validateStep, validateAllSteps } from '@utils/formValidation';
import {
  mapFormDataToCreateRequest,
  mapFormDataToPatchRequest,
  mapFormDataToSubmitRequest,
} from '@utils/formDataMapper';
import { convertBuildingLocalDateTimeToUtcIso } from '@utils/timeZone';
import { getStepFieldKeys, getAllStepFields, filterFormDataByStep } from '@utils/stepFieldKeys';
import { scrollAppToTop } from '@utils/scrollToTop';
import { telemetry } from '@/services/telemetry';
import { TELEMETRY_COMPONENT, EVENT_TYPES } from '@/constants';
import { isSpaceBookable } from '@/utils/spaceAvailability';
import { ROUTES } from '@routes/routeConstants';
import type { FieldDefinition, FieldOption, FormConfiguration } from '@store/models/booking';
import type { BuildingId } from '@store/models/ids';
import type { SpaceWithAvailability } from '@store/models/spaces/entities';

// Default page size for the available-spaces lazy-load (spec 103 FR-004 + Decision 10).
// Step 2 of the event creation flow fetches 10 cards per batch, then loads the next 10
// on scroll via the shared LoadSentinel.
const SPACES_PAGE_SIZE = 10;

// Arrangement style auto-selection rules applied when a space is adopted:
//  - Boardroom (fixed single arrangement): auto-select 'boardroom'.
//  - Single supported layout: auto-select that layout.
//  - Multiple supported layouts: leave empty so the user picks manually.
function computeInitialSetups(space: SpaceWithAvailability): string[] {
  if (space.isBoardRoom) {
    return ['boardroom'];
  }
  if (space.supportedRoomLayouts.length === 1) {
    const firstLayout = space.supportedRoomLayouts[0];
    return firstLayout ? [firstLayout.code] : [];
  }
  return [];
}

export function useBookingForm(options?: { isResuming?: boolean }) {
  const isResuming = options?.isResuming ?? false;
  const { t } = useTranslation('bookingForm');
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const formData = useAppSelector(selectFormData);
  const activeDraft = useAppSelector(selectActiveDraft);
  const currentStep = useAppSelector(selectCurrentStepIndex);
  const highestVisitedStep = useAppSelector(selectHighestVisitedStep);
  const selectedSpace = useAppSelector(selectSelectedSpace);
  const availableSpaces = useAppSelector(selectAvailableSpaces);
  const spacesLoading = useAppSelector(selectSpacesLoading);
  const spacesLoadingMore = useAppSelector(selectSpacesLoadingMore);
  const spacesHasMore = useAppSelector(selectSpacesHasMore);
  const spacesBatchError = useAppSelector(selectSpacesBatchError);
  const spacesContinuationToken = useAppSelector(selectSpacesContinuationToken);
  const submissionStatus = useAppSelector(selectSubmissionStatus);
  const apiError = useAppSelector(selectApiError);
  const eventId = useAppSelector(selectEventId);
  const etag = useAppSelector(selectEtag);
  const requestId = useAppSelector(selectRequestId);
  const validationErrors = useAppSelector(selectStepValidationErrors(currentStep));
  const hasUnsavedChanges = useAppSelector(selectHasUnsavedChanges);

  const [createEvent, { isLoading: isCreating }] = useCreateEventMutation();
  const [patchEvent, { isLoading: isPatching }] = usePatchEventMutation();
  const [submitEvent, { isLoading: isSubmitting }] = useSubmitEventMutation();
  const [deleteDraftEvent] = useDeleteDraftEventMutation();
  const [triggerGetSpaces] = spaceConfigServiceApi.useLazyGetSpacesQuery();
  const [triggerGetEvent] = eventServiceApi.useLazyGetEventByIdQuery();

  const isMutating = isCreating || isPatching || isSubmitting;
  const isSavingDraft = isCreating || isPatching;
  const isMutatingRef = useRef(isMutating);
  isMutatingRef.current = isMutating;

  // FR-002 + FR-011: persist the active amenity filter so the scroll-triggered
  // load-more dispatcher (loadMoreSpaces) can resend the same filter on every
  // batch. The amenity filter is held in the SpaceSelection layout's local state
  // today, so we mirror the most recent value here via the
  // handleAmenityFilterChange call site.
  const activeAmenitiesRef = useRef<string[]>([]);

  // Track whether the user navigated away from the review step during a draft resume.
  // When true, Back is disabled on the review step to prevent navigating to steps
  // the user did not explicitly visit in this session.
  const hasNavigatedFromReviewRef = useRef(false);

  const selectedStateCode = formData.stateProvince as string | undefined;

  const {
    data: allBuildingsData,
    isLoading: allBuildingsLoading,
    isFetching: allBuildingsFetching,
    isError: buildingsError,
    refetch: buildingsRefetch,
  } = useGetBuildingsQuery();

  const selectedBuildingId = formData.buildingId as string | undefined;

  const { data: buildingDetailData, isLoading: buildingDetailLoading } = useGetBuildingByIdQuery(
    selectedBuildingId as BuildingId,
    {
      skip: !selectedBuildingId,
    }
  );

  const regionName = DEFAULT_DATE_FORMAT_REGION;
  const { data: regionData } = useGetRegionConfigQuery(regionName);
  const buildingTimeZone = buildingDetailData?.data?.gmtZone;

  const prevStateRef = useRef(selectedStateCode);
  const prevBuildingRef = useRef(formData.buildingId as string | undefined);

  useEffect(() => {
    if (isResuming) return;
    dispatch(createDraft({ formId: bookingFormConfig.id }));

    // Seed default values from field definitions into formData
    const defaults: Record<string, unknown> = {};
    for (const step of bookingFormConfig.steps) {
      const allFields = [
        ...(step.fields ?? []),
        ...(step.sections?.flatMap((s) => s.fields) ?? []),
      ];
      for (const field of allFields) {
        if (field.defaultValue !== undefined) {
          defaults[field.key] = field.defaultValue;
        }
      }
    }
    if (Object.keys(defaults).length > 0) {
      dispatch(updateStepData({ stepIndex: 0, data: defaults }));
    }

    telemetry.trackPageView(TELEMETRY_COMPONENT, 'BookingsScreen');
  }, [dispatch, isResuming]);

  useEffect(() => {
    const handler = (e: BeforeUnloadEvent) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
      }
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [hasUnsavedChanges]);

  useEffect(() => {
    if (buildingsError) {
      dispatch(
        setApiError(
          String(
            t('bookingForm.errors.buildingsLoadFailed', {
              defaultValue: 'Failed to load buildings. Please try again.',
            })
          )
        )
      );
    }
  }, [buildingsError, dispatch, t]);

  useEffect(() => {
    if (
      prevStateRef.current !== undefined &&
      selectedStateCode !== undefined &&
      prevStateRef.current !== selectedStateCode
    ) {
      dispatch(
        updateStepData({
          stepIndex: 0,
          data: { buildingId: undefined },
        })
      );
    }
    prevStateRef.current = selectedStateCode;
  }, [selectedStateCode, dispatch]);

  useEffect(() => {
    const currentBuildingId = formData.buildingId as string | undefined;
    if (
      prevBuildingRef.current !== undefined &&
      currentBuildingId !== undefined &&
      prevBuildingRef.current !== currentBuildingId
    ) {
      dispatch(
        updateStepData({
          stepIndex: 2,
          data: {
            'sponsor.costCenter': undefined,
            businessGroupCode: undefined,
            businessJustification: undefined,
          },
        })
      );
    }
    // Sync buildingName into formData for API payloads
    if (currentBuildingId && allBuildingsData?.data) {
      const building = allBuildingsData.data.find((b) => b.buildingId === currentBuildingId);
      if (building) {
        dispatch(updateStepData({ stepIndex: 0, data: { buildingName: building.buildingName } }));
      }
    }
    prevBuildingRef.current = currentBuildingId;
  }, [formData.buildingId, allBuildingsData, dispatch]);

  useEffect(() => {
    const dateRange = formData.eventDateRange as { start?: string; end?: string } | undefined;
    const timeRange = formData.eventTimeRange as { start?: string; end?: string } | undefined;
    const timeZone = buildingDetailData?.data?.gmtZone;

    if (!dateRange?.start || !timeZone) {
      return;
    }

    const nextStart = convertBuildingLocalDateTimeToUtcIso(
      dateRange.start,
      timeRange?.start,
      timeZone
    );
    const nextEnd = convertBuildingLocalDateTimeToUtcIso(
      dateRange.end ?? dateRange.start,
      timeRange?.end,
      timeZone
    );

    if (formData.eventStartDate === nextStart && formData.eventEndDate === nextEnd) {
      return;
    }

    dispatch(
      updateStepData({
        stepIndex: 0,
        data: {
          eventStartDate: nextStart,
          eventEndDate: nextEnd,
        },
      })
    );
  }, [
    formData.eventDateRange,
    formData.eventTimeRange,
    formData.eventStartDate,
    formData.eventEndDate,
    buildingDetailData,
    dispatch,
  ]);

  useEffect(() => {
    const step = bookingFormConfig.steps[currentStep];
    if (!step || step.type !== 'form') return;

    const stepFields = [
      ...(step.fields ?? []),
      ...(step.sections?.flatMap((section) => section.fields) ?? []),
    ];

    const missingDefaults = stepFields.reduce<Record<string, unknown>>((defaults, field) => {
      if (field.defaultValue === undefined || formData[field.key] !== undefined) {
        return defaults;
      }

      defaults[field.key] = field.defaultValue;
      return defaults;
    }, {});

    if (Object.keys(missingDefaults).length === 0) return;

    dispatch(updateStepData({ stepIndex: currentStep, data: missingDefaults }));
  }, [currentStep, formData, dispatch]);

  const dynamicConfig = useMemo((): FormConfiguration => {
    // Derive unique state/province options from all buildings
    const stateMap = new Map<string, string>();
    if (allBuildingsData?.data) {
      for (const b of allBuildingsData.data) {
        if (b.stateCode && !stateMap.has(b.stateCode)) {
          stateMap.set(b.stateCode, b.stateName || b.stateCode);
        }
      }
    }
    const stateOptions: FieldOption[] = Array.from(stateMap.entries())
      .sort((a, b) => a[1].localeCompare(b[1]))
      .map(([code, name]) => ({ value: code, labelKey: name }));

    // Ensure the current selection is always in the options so the dropdown
    // shows the correct value even before the full list finishes loading.
    if (selectedStateCode && !stateOptions.find((o) => o.value === selectedStateCode)) {
      const stateName = (formData.stateName as string) || selectedStateCode;
      stateOptions.unshift({ value: selectedStateCode, labelKey: stateName });
    }

    // Filter buildings by selected state
    const filteredBuildings =
      selectedStateCode && allBuildingsData?.data
        ? allBuildingsData.data.filter((b) => b.stateCode === selectedStateCode)
        : [];
    const buildingOptions: FieldOption[] = filteredBuildings.map((b) => ({
      value: b.buildingId,
      labelKey: b.buildingName,
    }));

    if (selectedBuildingId && !buildingOptions.find((o) => o.value === selectedBuildingId)) {
      const bName = (formData.buildingName as string) || selectedBuildingId;
      buildingOptions.unshift({ value: selectedBuildingId, labelKey: bName });
    }

    const selectedBuilding = buildingDetailData?.data;
    const buildingReady = !!selectedBuilding && !buildingDetailLoading;

    const businessGroupOptions: FieldOption[] = selectedBuilding?.businessGroupConfig
      ?.businessGroups
      ? selectedBuilding.businessGroupConfig.businessGroups.map((group) => ({
          value: group.code,
          labelKey: group.label,
        }))
      : [];
    const showBusinessGroup =
      buildingReady && selectedBuilding?.businessGroupConfig?.required === true;
    const showCostCenter = buildingReady && selectedBuilding?.requireCostCenter === true;

    const updatedSteps = bookingFormConfig.steps.map((step, index) => {
      if (index === 0 && step.sections) {
        return {
          ...step,
          sections: step.sections.map((section) => ({
            ...section,
            fields: section.fields.map((field) => {
              if (field.key === 'stateProvince') {
                return { ...field, options: stateOptions, disabled: allBuildingsLoading };
              }
              if (field.key === 'buildingId') {
                return {
                  ...field,
                  options: buildingOptions,
                  disabled: allBuildingsLoading || allBuildingsFetching || !selectedStateCode,
                };
              }
              return field;
            }),
          })),
        };
      }
      if (index === 2 && step.sections) {
        return {
          ...step,
          sections: step.sections.map((section) => ({
            ...section,
            fields: section.fields
              .filter((field) => {
                if (field.key === 'businessGroupCode') return showBusinessGroup;
                if (field.key === 'businessJustification') return showBusinessGroup;
                if (field.key === 'sponsor.costCenter') return showCostCenter;
                return true;
              })
              .map((field) => {
                if (field.key === 'visitPurposeCode') {
                  return {
                    ...field,
                    options: VISIT_PURPOSE_OPTIONS,
                    validation: [
                      {
                        type: 'required' as const,
                        messageKey: 'bookingForm.validation.required',
                      },
                    ],
                  };
                }
                if (field.key === 'businessGroupCode') {
                  return {
                    ...field,
                    options: businessGroupOptions,
                    validation: showBusinessGroup
                      ? [
                          {
                            type: 'required' as const,
                            messageKey: 'bookingForm.validation.required',
                          },
                        ]
                      : [],
                  };
                }
                if (field.key === 'businessJustification') {
                  return {
                    ...field,
                    validation: showBusinessGroup
                      ? [
                          {
                            type: 'required' as const,
                            messageKey: 'bookingForm.validation.required',
                          },
                        ]
                      : [],
                  };
                }
                if (field.key === 'sponsor.costCenter') {
                  return {
                    ...field,
                    validation: showCostCenter
                      ? [
                          {
                            type: 'required' as const,
                            messageKey: 'bookingForm.validation.required',
                          },
                        ]
                      : [],
                  };
                }
                return field;
              }),
          })),
        };
      }
      if (index === 3 && step.sections) {
        const policiesUrl = String(selectedBuilding?.policiesUrl ?? DEFAULT_POLICIES_URL);
        return {
          ...step,
          sections: step.sections.map((section) => ({
            ...section,
            fields: section.fields.map((field) => {
              if (field.key === 'policyAcknowledgement') {
                return { ...field, linkUrl: policiesUrl };
              }
              return field;
            }),
          })),
        };
      }
      return step;
    });
    const regionQuickLinks = regionData?.data?.employeeQuickLinks?.map((link) => ({
      labelKey: link.name,
      url: link.value,
      icon: link.type === 'link' ? 'GlobeRegular' : undefined,
    }));

    return {
      ...bookingFormConfig,
      steps: updatedSteps,
      ...(regionQuickLinks && regionQuickLinks.length > 0 && { quickLinks: regionQuickLinks }),
    };
  }, [
    allBuildingsData,
    allBuildingsLoading,
    allBuildingsFetching,
    buildingDetailData,
    buildingDetailLoading,
    regionData,
    selectedStateCode,
    selectedBuildingId,
    formData.stateName,
    formData.buildingName,
  ]);

  const currentStepFields = useMemo((): FieldDefinition[] => {
    const step = dynamicConfig.steps[currentStep];
    if (!step) return [];
    if (step.sections) return step.sections.flatMap((s) => s.fields);
    return step.fields ?? [];
  }, [currentStep, dynamicConfig]);

  const filterFields = useMemo((): FieldDefinition[] => {
    const step = dynamicConfig.steps[0];
    if (!step) return [];
    if (step.sections) return step.sections.flatMap((s) => s.fields);
    return step.fields ?? [];
  }, [dynamicConfig]);

  const translateFn = useCallback(
    (key: string, options?: Record<string, unknown>) =>
      String(t(key, options as Record<string, string>)),
    [t]
  );

  const handleFieldChange = useCallback(
    (key: string, value: unknown) => {
      dispatch(updateStepData({ stepIndex: currentStep, data: { [key]: value } }));
      dispatch(setApiError(null));
      if (validationErrors[key]) {
        const { [key]: _, ...remaining } = validationErrors;
        dispatch(setValidationErrors({ stepIndex: currentStep, errors: remaining }));
      }
    },
    [dispatch, currentStep, validationErrors]
  );

  const handleInlineFieldChange = useCallback(
    (key: string, value: unknown) => {
      dispatch(updateStepData({ stepIndex: 0, data: { [key]: value } }));
      dispatch(setApiError(null));
    },
    [dispatch]
  );

  const handleUpdateFilters = useCallback(async (): Promise<boolean> => {
    const result = validateStep(filterFields, formData, translateFn);
    if (!result.isValid) {
      dispatch(setValidationErrors({ stepIndex: 0, errors: result.errors }));
      return false;
    }
    dispatch(clearValidationErrors({ stepIndex: 0 }));

    telemetry.trackEvent(TELEMETRY_COMPONENT, EVENT_TYPES.STEP_CHANGE, {
      action: 'inline-filter-update',
      fromStep: '1',
      toStep: '1',
    });
    if (eventId) {
      const formBuildingId = formData.buildingId as BuildingId;
      try {
        const fieldKeys = getStepFieldKeys(bookingFormConfig, 0);
        const stepFields = getAllStepFields(bookingFormConfig, 0);
        const dataToMap = {
          ...filterFormDataByStep(formData, fieldKeys, stepFields, formData),
          // `buildingName` is derived (not a configured form field) so the step filter strips
          // it; surface it explicitly so the unified-space patch carries the new display name
          // alongside `space.buildingId`.
          buildingName: formData.buildingName,
        };
        const patchBody = mapFormDataToPatchRequest(dataToMap, {
          buildingTimeZone,
        });
        // spec 101 US4: persist current step id so draft tile parity holds.
        const currentStepIdForFilters = bookingFormConfig.steps[currentStep]?.id;
        if (currentStepIdForFilters) {
          (patchBody as { currentStep?: string }).currentStep = currentStepIdForFilters;
        }
        const response = await patchEvent({
          params: { eventId, buildingId: formBuildingId, etag: etag ?? undefined },
          body: patchBody,
        }).unwrap();
        // Capture refreshed concurrency token for later PATCH/SUBMIT calls
        if (response?.etag) {
          dispatch(setEtag(response.etag));
        }
      } catch {
        // Non-blocking — continue with spaces re-fetch
      }
    }

    // Clear selected space since filter criteria changed
    dispatch(selectSpaceAction(null));
    dispatch(updateStepData({ stepIndex: 1, data: { selectedSpace: null, selectedSetups: [] } }));

    // Re-fetch available spaces with updated params
    const buildingId = formData.buildingId as BuildingId;
    const dateRange = formData.eventDateRange as { start?: string; end?: string } | undefined;
    const timeRange = formData.eventTimeRange as { start?: string; end?: string } | undefined;
    const attendeeCount = Number(formData.numberOfAttendees);
    // FR-011: server-side reset — discard loaded window before issuing the first-batch request
    dispatch(resetSpacesPagination());
    dispatch(setSpacesLoading(true));
    dispatch(setApiError(null));
    try {
      const spacesResult = await triggerGetSpaces({
        buildingId,
        ...(dateRange?.start ? { startDate: dateRange.start.slice(0, 10) } : {}),
        ...(dateRange?.end ? { endDate: dateRange.end.slice(0, 10) } : {}),
        ...(timeRange?.start ? { startTime: timeRange.start } : {}),
        ...(timeRange?.end ? { endTime: timeRange.end } : {}),
        ...(Number.isFinite(attendeeCount) && attendeeCount > 0 ? { capacity: attendeeCount } : {}),
        pageSize: SPACES_PAGE_SIZE,
      }).unwrap();
      dispatch(setAvailableSpaces(spacesResult.data));
      dispatch(setSpacesContinuationToken(spacesResult.pagination?.continuationToken ?? null));
      dispatch(setSpacesHasMore(Boolean(spacesResult.pagination?.hasMore)));
      return true;
    } catch {
      dispatch(setApiError(String(t('bookingForm.space.errorLoading'))));
      dispatch(setAvailableSpaces([]));
      dispatch(setSpacesContinuationToken(null));
      dispatch(setSpacesHasMore(false));
      return false;
    } finally {
      dispatch(setSpacesLoading(false));
    }
  }, [
    filterFields,
    formData,
    translateFn,
    dispatch,
    eventId,
    etag,
    patchEvent,
    triggerGetSpaces,
    t,
    buildingTimeZone,
    currentStep,
  ]);

  const patchCurrentStep = useCallback(
    async (useFullData = false): Promise<boolean> => {
      if (!eventId) return true;
      const formBuildingId = formData.buildingId as BuildingId;
      try {
        const currentStepConfig = bookingFormConfig.steps[currentStep];
        const isCustomStep = currentStepConfig?.type === 'space-selection';
        let dataToMap = formData;
        if (!useFullData && isCustomStep) {
          // Space-selection step: send space fields AND the building so the unified-space
          // contract emits both `space.buildingId` and (when applicable) `space.spaceId`.
          // This is what carries a deferred cross-building move through to the backend in
          // the same PATCH that picks the new space.
          dataToMap = {
            buildingId: formData.buildingId,
            buildingName: formData.buildingName,
            selectedSpace: formData.selectedSpace,
            selectedSetups: formData.selectedSetups,
          };
        } else if (!useFullData) {
          const fieldKeys = getStepFieldKeys(bookingFormConfig, currentStep);
          const stepFields = getAllStepFields(bookingFormConfig, currentStep);
          dataToMap = {
            ...filterFormDataByStep(formData, fieldKeys, stepFields, formData),
            // `buildingName` is derived (not a configured field) so the step filter strips it.
            // Carry it through whenever the where-step PATCH includes a building change so the
            // backend receives the new display name alongside `space.buildingId`.
            ...(currentStep === 0 ? { buildingName: formData.buildingName } : {}),
          };
        }
        const patchBody = mapFormDataToPatchRequest(dataToMap, {
          buildingTimeZone,
        });
        // spec 101 US4 (FR-009, FR-010): persist the step id on every PATCH so
        // draft tiles render "Step N of M" in parity with the resume step.
        const currentStepIdForPatch = bookingFormConfig.steps[currentStep]?.id;
        if (currentStepIdForPatch) {
          (patchBody as { currentStep?: string }).currentStep = currentStepIdForPatch;
        }
        const startTime = Date.now();
        const response = await patchEvent({
          params: { eventId, buildingId: formBuildingId, etag: etag ?? undefined },
          body: patchBody,
        }).unwrap();
        telemetry.trackEvent(TELEMETRY_COMPONENT, EVENT_TYPES.API_CALL, {
          apiEndpoint: 'PATCH /v1/events',
          durationMs: String(Date.now() - startTime),
          status: 'success',
          eventId: eventId as string,
        });
        if (response.etag) {
          dispatch(setEtag(response.etag));
        }
        return true;
      } catch (err: unknown) {
        const error = err as {
          status?: number;
          data?: { error?: { code?: string; message?: string } };
        };
        if (error.status === 412) {
          dispatch(
            setApiError(
              String(
                t('bookingForm.errors.eventModifiedExternally', {
                  defaultValue: 'This event was modified in another session. Please reload.',
                })
              )
            )
          );
        } else if (error.status === 403) {
          dispatch(
            setApiError(
              String(
                t('bookingForm.errors.eventNoLongerEditable', {
                  defaultValue: 'This event is no longer editable.',
                })
              )
            )
          );
          void navigate('/');
        } else if (error.status === 428) {
          dispatch(
            setApiError(
              String(
                t('bookingForm.errors.etagMissing', {
                  defaultValue: 'Concurrency token missing. Please try again.',
                })
              )
            )
          );
        } else {
          dispatch(
            setApiError(
              String(
                t('bookingForm.messages.apiError', {
                  defaultValue: 'Something went wrong. Please try again.',
                })
              )
            )
          );
        }
        return false;
      }
    },
    [eventId, etag, formData, currentStep, patchEvent, dispatch, t, navigate, buildingTimeZone]
  );

  const handleNext = useCallback(async () => {
    if (isMutatingRef.current) return;

    const step = dynamicConfig.steps[currentStep];
    if (!step) return;

    if (step.type === 'form' && currentStepFields.length > 0) {
      const result = validateStep(currentStepFields, formData, translateFn);
      if (!result.isValid) {
        dispatch(setValidationErrors({ stepIndex: currentStep, errors: result.errors }));
        return;
      }
      dispatch(clearValidationErrors({ stepIndex: currentStep }));
    }

    dispatch(setApiError(null));
    const dateRange = formData.eventDateRange as { start?: string; end?: string } | undefined;

    if (currentStep === 0) {
      const buildingId = formData.buildingId as BuildingId;

      if (eventId) {
        // Resumed draft — event already exists, use PATCH instead of POST
        await patchCurrentStep();

        dispatch(commitStep({ stepIndex: 0 }));
        dispatch(goToStep({ stepIndex: 1 }));
        scrollAppToTop();
        telemetry.trackEvent(TELEMETRY_COMPONENT, EVENT_TYPES.STEP_CHANGE, {
          fromStep: '0',
          toStep: '1',
        });

        const timeRange = formData.eventTimeRange as { start?: string; end?: string } | undefined;
        const attendeeCount = Number(formData.numberOfAttendees);
        dispatch(setSpacesLoading(true));
        try {
          const spacesResult = await triggerGetSpaces({
            buildingId,
            ...(dateRange?.start ? { startDate: dateRange.start.slice(0, 10) } : {}),
            ...(dateRange?.end ? { endDate: dateRange.end.slice(0, 10) } : {}),
            ...(timeRange?.start ? { startTime: timeRange.start } : {}),
            ...(timeRange?.end ? { endTime: timeRange.end } : {}),
            ...(Number.isFinite(attendeeCount) && attendeeCount > 0
              ? { capacity: attendeeCount }
              : {}),
            pageSize: SPACES_PAGE_SIZE,
          }).unwrap();
          dispatch(setAvailableSpaces(spacesResult.data));
          dispatch(setSpacesContinuationToken(spacesResult.pagination?.continuationToken ?? null));
          dispatch(setSpacesHasMore(Boolean(spacesResult.pagination?.hasMore)));
        } catch {
          dispatch(setApiError(String(t('bookingForm.space.errorLoading'))));
          dispatch(setAvailableSpaces([]));
          dispatch(setSpacesContinuationToken(null));
          dispatch(setSpacesHasMore(false));
        } finally {
          dispatch(setSpacesLoading(false));
        }
        return;
      }

      try {
        // spec 101 US4: include currentStep id so draft tiles can show the
        // correct "Step N of M" matching the resume step.
        const createReq = mapFormDataToCreateRequest(
          { ...formData, currentStep: bookingFormConfig.steps[currentStep]?.id },
          {
            buildingTimeZone,
          }
        );
        dispatch(setRequestId(createReq.requestId));
        const startTime = Date.now();
        const response = await createEvent({ body: createReq, buildingId }).unwrap();
        telemetry.trackEvent(TELEMETRY_COMPONENT, EVENT_TYPES.API_CALL, {
          apiEndpoint: 'POST /v1/events',
          durationMs: String(Date.now() - startTime),
          status: 'success',
          eventId: response.data.eventId as string,
        });
        dispatch(setEventId(response.data.eventId));
        if (response.etag) {
          dispatch(setEtag(response.etag));
        }
      } catch (err: unknown) {
        const error = err as {
          status?: number;
          data?: {
            detail?: string;
            error?: {
              code?: string;
              message?: string;
              details?: string | Record<string, string[]>;
            };
          };
        };
        const details = error.data?.error?.details;
        const backendMessage =
          (typeof details === 'string'
            ? details
            : details && typeof details === 'object'
              ? Object.values(details).flat()[0]
              : undefined) ??
          error.data?.detail ??
          error.data?.error?.message;
        dispatch(
          setApiError(
            backendMessage ??
              String(
                t('bookingForm.messages.apiError', {
                  defaultValue: 'Failed to create event. Please try again.',
                })
              )
          )
        );
        return;
      }

      dispatch(commitStep({ stepIndex: 0 }));
      dispatch(goToStep({ stepIndex: 1 }));
      scrollAppToTop();
      telemetry.trackEvent(TELEMETRY_COMPONENT, EVENT_TYPES.STEP_CHANGE, {
        fromStep: '0',
        toStep: '1',
      });

      const timeRange = formData.eventTimeRange as { start?: string; end?: string } | undefined;
      const attendeeCount = Number(formData.numberOfAttendees);
      dispatch(setSpacesLoading(true));
      try {
        const spacesResult = await triggerGetSpaces({
          buildingId,
          ...(dateRange?.start ? { startDate: dateRange.start.slice(0, 10) } : {}),
          ...(dateRange?.end ? { endDate: dateRange.end.slice(0, 10) } : {}),
          ...(timeRange?.start ? { startTime: timeRange.start } : {}),
          ...(timeRange?.end ? { endTime: timeRange.end } : {}),
          ...(Number.isFinite(attendeeCount) && attendeeCount > 0
            ? { capacity: attendeeCount }
            : {}),
          pageSize: SPACES_PAGE_SIZE,
        }).unwrap();
        dispatch(setAvailableSpaces(spacesResult.data));
        dispatch(setSpacesContinuationToken(spacesResult.pagination?.continuationToken ?? null));
        dispatch(setSpacesHasMore(Boolean(spacesResult.pagination?.hasMore)));
      } catch {
        dispatch(setApiError(String(t('bookingForm.space.errorLoading'))));
        dispatch(setAvailableSpaces([]));
        dispatch(setSpacesContinuationToken(null));
        dispatch(setSpacesHasMore(false));
      } finally {
        dispatch(setSpacesLoading(false));
      }
      return;
    }

    await patchCurrentStep();

    dispatch(commitStep({ stepIndex: currentStep }));

    const nextStep = currentStep + 1;
    dispatch(goToStep({ stepIndex: nextStep }));
    scrollAppToTop();
    telemetry.trackEvent(TELEMETRY_COMPONENT, EVENT_TYPES.STEP_CHANGE, {
      fromStep: String(currentStep),
      toStep: String(nextStep),
    });
  }, [
    currentStep,
    currentStepFields,
    formData,
    dispatch,
    createEvent,
    eventId,
    patchCurrentStep,
    triggerGetSpaces,
    translateFn,
    dynamicConfig,
    t,
    buildingTimeZone,
  ]);

  const handleBack = useCallback(async () => {
    if (currentStep > 0) {
      await patchCurrentStep();
      const prevStep = currentStep - 1;
      telemetry.trackEvent(TELEMETRY_COMPONENT, EVENT_TYPES.STEP_CHANGE, {
        fromStep: String(currentStep),
        toStep: String(prevStep),
      });
      dispatch(goToStep({ stepIndex: prevStep }));
      scrollAppToTop();

      // Re-fetch spaces when navigating back to the space selection step
      // if they haven't been loaded yet (e.g. draft resume skipped to step 2+).
      // FR-015: When the loaded window IS still in memory, do NOT re-fetch —
      // the existing batches and the user's current selection MUST be preserved
      // so the scroll-load mechanism resumes where they left off.
      if (prevStep === 1 && availableSpaces.length === 0) {
        const buildingId = formData.buildingId as BuildingId;
        const dateRange = formData.eventDateRange as { start?: string; end?: string } | undefined;
        const timeRange = formData.eventTimeRange as { start?: string; end?: string } | undefined;
        const attendeeCount = Number(formData.numberOfAttendees);
        if (buildingId) {
          dispatch(setSpacesLoading(true));
          try {
            const spacesResult = await triggerGetSpaces({
              buildingId,
              ...(dateRange?.start ? { startDate: dateRange.start.slice(0, 10) } : {}),
              ...(dateRange?.end ? { endDate: dateRange.end.slice(0, 10) } : {}),
              ...(timeRange?.start ? { startTime: timeRange.start } : {}),
              ...(timeRange?.end ? { endTime: timeRange.end } : {}),
              ...(Number.isFinite(attendeeCount) && attendeeCount > 0
                ? { capacity: attendeeCount }
                : {}),
              pageSize: SPACES_PAGE_SIZE,
            }).unwrap();
            dispatch(setAvailableSpaces(spacesResult.data));
            dispatch(
              setSpacesContinuationToken(spacesResult.pagination?.continuationToken ?? null)
            );
            dispatch(setSpacesHasMore(Boolean(spacesResult.pagination?.hasMore)));
          } catch {
            dispatch(setApiError(String(t('bookingForm.space.errorLoading'))));
            dispatch(setAvailableSpaces([]));
            dispatch(setSpacesContinuationToken(null));
            dispatch(setSpacesHasMore(false));
          } finally {
            dispatch(setSpacesLoading(false));
          }
        }
      }
    }
  }, [
    currentStep,
    dispatch,
    patchCurrentStep,
    availableSpaces.length,
    formData,
    triggerGetSpaces,
    t,
  ]);

  const handleStepClick = useCallback(
    async (stepIndex: number) => {
      // Persist the current step before jumping via the step indicator or a
      // section "Edit" link. Without this, edits made after navigating here
      // (e.g. changing the date on a resumed past-dated draft) are never
      // PATCHed, so submit — which does not resend dates — validates the stale
      // stored value. Mirrors handleNext/handleBack.
      if (currentStep !== stepIndex) {
        await patchCurrentStep();
      }
      // Mark that user has navigated away from review (e.g. via Edit on a section)
      if (isResuming) {
        hasNavigatedFromReviewRef.current = true;
      }
      dispatch(goToStep({ stepIndex }));
      scrollAppToTop();
    },
    [dispatch, isResuming, currentStep, patchCurrentStep]
  );

  // Adopt an available space as the current selection: resolve it, emit the
  // SPACE_SELECT telemetry once, then atomically store the space and its setup
  // codes so the persisted space and arrangement always match (Bug 16203566).
  // When setupCodes is omitted, the space's default arrangement is applied.
  const adoptSpace = useCallback(
    (spaceId: string, setupCodes?: string[]): boolean => {
      const space = availableSpaces.find((s) => s.spaceId === spaceId);
      if (!space) return false;

      telemetry.trackEvent(TELEMETRY_COMPONENT, EVENT_TYPES.SPACE_SELECT, {
        spaceId: space.spaceId,
      });

      const spaceDetails = {
        id: space.spaceId,
        name: space.spaceName,
        imageUrl: space.imageUrl,
        floor: space.floor,
        roomNumber: space.roomNumber,
        roomCapacity: space.roomCapacity,
      };

      const setups = setupCodes ?? computeInitialSetups(space);

      dispatch(selectSpaceAction(spaceDetails));
      // Keep step state updates atomic so the UI never renders a transient
      // "selected space + mismatched setups" frame.
      dispatch(
        updateStepData({
          stepIndex: 1,
          data: { selectedSpace: spaceDetails, selectedSetups: setups },
        })
      );
      return true;
    },
    [availableSpaces, dispatch]
  );

  const handleSelectSpace = useCallback(
    (spaceId: string) => {
      if (isMutatingRef.current) return;

      // Toggle: deselect if already selected
      if (selectedSpace?.id === spaceId) {
        dispatch(selectSpaceAction(null));
        dispatch(
          updateStepData({ stepIndex: 1, data: { selectedSpace: null, selectedSetups: [] } })
        );
        return;
      }

      // Space selection is stored locally only — persisted on "Next" or "Save as Draft"
      // via patchCurrentStep (FR-004).
      adoptSpace(spaceId);
    },
    [selectedSpace, dispatch, adoptSpace]
  );

  const handleSetupChange = useCallback(
    (spaceId: string, setupCodes: string[]) => {
      if (isMutatingRef.current) return;

      // Setup chosen on the already-selected space → just update its setups.
      if (selectedSpace?.id === spaceId) {
        dispatch(updateStepData({ stepIndex: 1, data: { selectedSetups: setupCodes } }));
        return;
      }

      // Setup chosen on a different available space → adopt that space so the
      // persisted space and its arrangement codes always match (Bug 16203566).
      const target = availableSpaces.find((s) => s.spaceId === spaceId);
      if (!target || !isSpaceBookable(target)) return;

      const setups = setupCodes.length > 0 ? setupCodes : computeInitialSetups(target);
      adoptSpace(spaceId, setups);
    },
    [availableSpaces, selectedSpace, dispatch, adoptSpace]
  );

  const handleSaveAsDraft = useCallback(async () => {
    if (eventId) {
      // Reuse step-scoped patch logic — sends only current step's fields
      const saved = await patchCurrentStep();
      if (!saved) return;
      dispatch(saveAsDraft());
      void navigate(ROUTES.BOOKINGS);
      return;
    }

    // No eventId — POST to create a new draft event (step 0 only)
    if (currentStep === 0) {
      const step = dynamicConfig.steps[0];
      if (step) {
        const stepFields: FieldDefinition[] = step.sections
          ? step.sections.flatMap((s) => s.fields)
          : (step.fields ?? []);

        // FR-005b: Save as draft bypasses Step 1 required-field and end-after-start
        // cross-field validators. Only the Next/advance action enforces them.
        const bypassedForDraft = new Set([
          'required',
          'dateRangeComplete',
          'timeRangeComplete',
          'dateRangeOrder',
          'timeRangeOrder',
        ]);
        const draftFields = stepFields.map((field) => ({
          ...field,
          validation: field.validation?.filter((r) => !bypassedForDraft.has(r.type)),
        }));

        const result = validateStep(draftFields, formData, translateFn);
        if (!result.isValid) {
          dispatch(setValidationErrors({ stepIndex: currentStep, errors: result.errors }));
          return;
        }
        dispatch(clearValidationErrors({ stepIndex: currentStep }));
      }

      const buildingId = formData.buildingId as BuildingId;
      try {
        // spec 101 US4: include currentStep id on the draft-save POST.
        const createReq = mapFormDataToCreateRequest({
          ...formData,
          currentStep: bookingFormConfig.steps[currentStep]?.id,
        });
        dispatch(setRequestId(createReq.requestId));
        const response = await createEvent({ body: createReq, buildingId }).unwrap();
        dispatch(setEventId(response.data.eventId));
        if (response.etag) {
          dispatch(setEtag(response.etag));
        }
        dispatch(saveAsDraft());
        void navigate(ROUTES.BOOKINGS);
      } catch (err: unknown) {
        const error = err as {
          status?: number;
          data?: {
            detail?: string;
            error?: {
              code?: string;
              message?: string;
              details?: string | Record<string, string[]>;
            };
          };
        };
        const details = error.data?.error?.details;
        const backendMessage =
          (typeof details === 'string'
            ? details
            : details && typeof details === 'object'
              ? Object.values(details).flat()[0]
              : undefined) ??
          error.data?.detail ??
          error.data?.error?.message;
        dispatch(
          setApiError(
            backendMessage ??
              String(
                t('bookingForm.messages.apiError', {
                  defaultValue: 'Failed to create event. Please try again.',
                })
              )
          )
        );
      }
    }
  }, [
    eventId,
    formData,
    currentStep,
    dynamicConfig,
    dispatch,
    patchCurrentStep,
    createEvent,
    navigate,
    t,
    translateFn,
  ]);

  const handleDiscard = useCallback(async () => {
    telemetry.trackEvent(TELEMETRY_COMPONENT, EVENT_TYPES.FORM_ABANDON, {
      step: String(currentStep),
      eventId: (eventId as string) ?? '',
    });
    if (eventId) {
      const buildingId = formData.buildingId as BuildingId;
      try {
        await deleteDraftEvent({ eventId, buildingId }).unwrap();
      } catch {
        // Discard locally even if API fails
      }
    }
    dispatch(discardDraft());
    dispatch(createDraft({ formId: bookingFormConfig.id }));
  }, [eventId, currentStep, formData, dispatch, deleteDraftEvent]);

  const handleSubmit = useCallback(async () => {
    if (isMutatingRef.current) return;

    if (activeDraft?.status && activeDraft.status !== 'draft') {
      dispatch(setSubmissionStatus('error'));
      dispatch(setApiError(String(t('bookingForm.errors.eventNoLongerEditable'))));
      return;
    }

    if (!eventId) {
      dispatch(setSubmissionStatus('error'));
      dispatch(setApiError(String(t('bookingForm.messages.submitRetry'))));
      return;
    }

    const buildingId = formData.buildingId as BuildingId;
    dispatch(submitForm());
    dispatch(setApiError(null));
    telemetry.trackEvent(TELEMETRY_COMPONENT, EVENT_TYPES.FORM_SUBMIT, {
      eventId: (eventId as string) ?? '',
    });

    try {
      const startTime = Date.now();
      const response = await submitEvent({
        eventId,
        buildingId,
        etag: etag ?? undefined,
        body: mapFormDataToSubmitRequest(formData),
      }).unwrap();

      telemetry.trackEvent(TELEMETRY_COMPONENT, EVENT_TYPES.API_CALL, {
        apiEndpoint: 'POST /v1/events/{eventId}/submit',
        durationMs: String(Date.now() - startTime),
        status: 'success',
        eventId: eventId as string,
      });

      if (response.etag) {
        dispatch(setEtag(response.etag));
      }
      dispatch(setSubmissionStatus('submitted'));
      scrollAppToTop();
    } catch (err: unknown) {
      const error = err as {
        status?: number;
        data?: {
          detail?: string;
          error?: {
            code?: string;
            message?: string;
            details?: string | Record<string, string[]>;
          };
        };
      };

      // A 409 on submit likely means a prior attempt already succeeded.
      // Verify by fetching the event's current state before surfacing an error.
      if (error.status === 409) {
        try {
          const eventResult = await triggerGetEvent({ eventId }).unwrap();
          if (eventResult.data?.eventState === 'SUBMITTED') {
            dispatch(setSubmissionStatus('submitted'));
            scrollAppToTop();
            return;
          }
        } catch {
          // Verification failed — fall through to surface original error
        }
      }

      let messageKey = 'bookingForm.messages.submitRetry';
      if (error.status === 403) {
        messageKey = 'bookingForm.errors.submitForbidden';
      } else if (error.status === 409) {
        messageKey = 'bookingForm.errors.eventNoLongerEditable';
      } else if (error.status === 412) {
        messageKey = 'bookingForm.errors.eventModifiedExternally';
      } else if (error.status === 428) {
        messageKey = 'bookingForm.errors.etagMissing';
      }

      const details = error.data?.error?.details;
      const backendMessage =
        (typeof details === 'string'
          ? details
          : details && typeof details === 'object'
            ? Object.values(details).flat()[0]
            : undefined) ??
        error.data?.detail ??
        error.data?.error?.message;
      dispatch(setSubmissionStatus('error'));
      dispatch(setApiError(backendMessage ?? String(t(messageKey))));
    }
  }, [activeDraft?.status, eventId, etag, formData, dispatch, submitEvent, triggerGetEvent, t]);

  const stepCompletionMap = useMemo(
    () => validateAllSteps(dynamicConfig, formData, translateFn),
    [dynamicConfig, formData, translateFn]
  );

  const canSubmit =
    activeDraft?.status === 'draft' &&
    stepCompletionMap.allComplete &&
    !!formData.policyAcknowledgement;

  const canSaveDraft = useMemo(() => {
    if (submissionStatus === 'submitting' || isSavingDraft) return false;
    if (currentStep === 0 && !eventId) {
      return Boolean(formData.stateProvince) && Boolean(formData.buildingId);
    }
    return true;
  }, [
    submissionStatus,
    isSavingDraft,
    currentStep,
    eventId,
    formData.stateProvince,
    formData.buildingId,
  ]);

  const handleBookAnother = useCallback(() => {
    dispatch(createDraft({ formId: bookingFormConfig.id }));
  }, [dispatch]);

  const handleGoHome = useCallback(() => {
    void navigate('/');
  }, [navigate]);

  const handleRetrySpaces = useCallback(async () => {
    dispatch(setSpacesLoading(true));
    dispatch(setApiError(null));
    const dateRange = formData.eventDateRange as { start?: string; end?: string } | undefined;
    const timeRange = formData.eventTimeRange as { start?: string; end?: string } | undefined;
    const attendeeCount = Number(formData.numberOfAttendees);
    try {
      const result = await triggerGetSpaces({
        buildingId: formData.buildingId as BuildingId,
        ...(dateRange?.start ? { startDate: dateRange.start.slice(0, 10) } : {}),
        ...(dateRange?.end ? { endDate: dateRange.end.slice(0, 10) } : {}),
        ...(timeRange?.start ? { startTime: timeRange.start } : {}),
        ...(timeRange?.end ? { endTime: timeRange.end } : {}),
        ...(Number.isFinite(attendeeCount) && attendeeCount > 0 ? { capacity: attendeeCount } : {}),
        pageSize: SPACES_PAGE_SIZE,
      }).unwrap();
      dispatch(setAvailableSpaces(result.data));
      dispatch(setSpacesContinuationToken(result.pagination?.continuationToken ?? null));
      dispatch(setSpacesHasMore(Boolean(result.pagination?.hasMore)));
    } catch {
      dispatch(setApiError(String(t('bookingForm.space.errorLoading'))));
      dispatch(setAvailableSpaces([]));
      dispatch(setSpacesContinuationToken(null));
      dispatch(setSpacesHasMore(false));
    } finally {
      dispatch(setSpacesLoading(false));
    }
  }, [formData, dispatch, triggerGetSpaces, t]);

  const handleAmenityFilterChange = useCallback(
    async (amenities: string[]) => {
      activeAmenitiesRef.current = amenities;
      // FR-011: server-side reset — discard loaded window before issuing the first-batch request
      dispatch(resetSpacesPagination());
      dispatch(setSpacesLoading(true));
      dispatch(setApiError(null));
      const dateRange = formData.eventDateRange as { start?: string; end?: string } | undefined;
      const timeRange = formData.eventTimeRange as { start?: string; end?: string } | undefined;
      const attendeeCount = Number(formData.numberOfAttendees);
      try {
        const result = await triggerGetSpaces({
          buildingId: formData.buildingId as BuildingId,
          ...(dateRange?.start ? { startDate: dateRange.start.slice(0, 10) } : {}),
          ...(dateRange?.end ? { endDate: dateRange.end.slice(0, 10) } : {}),
          ...(timeRange?.start ? { startTime: timeRange.start } : {}),
          ...(timeRange?.end ? { endTime: timeRange.end } : {}),
          ...(amenities.length > 0 ? { amenities: amenities.join(',') } : {}),
          ...(Number.isFinite(attendeeCount) && attendeeCount > 0
            ? { capacity: attendeeCount }
            : {}),
          pageSize: SPACES_PAGE_SIZE,
        }).unwrap();
        dispatch(setAvailableSpaces(result.data));
        dispatch(setSpacesContinuationToken(result.pagination?.continuationToken ?? null));
        dispatch(setSpacesHasMore(Boolean(result.pagination?.hasMore)));
      } catch {
        dispatch(setApiError(String(t('bookingForm.space.errorLoading'))));
      } finally {
        dispatch(setSpacesLoading(false));
      }
    },
    [formData, dispatch, triggerGetSpaces, t]
  );

  // FR-002 + FR-013 + FR-019: scroll-triggered next-batch fetch. Coalesces concurrent
  // attempts via the spacesLoadingMore guard, emits per-batch telemetry, and surfaces
  // failures as an inline batch error (FR-012 subsequent-batch path) without disturbing
  // the previously loaded cards or current selection.
  const loadMoreSpaces = useCallback(async () => {
    if (spacesLoadingMore || spacesLoading || !spacesHasMore || !spacesContinuationToken) {
      return;
    }

    const dateRange = formData.eventDateRange as { start?: string; end?: string } | undefined;
    const timeRange = formData.eventTimeRange as { start?: string; end?: string } | undefined;
    const attendeeCount = Number(formData.numberOfAttendees);
    const buildingId = formData.buildingId as BuildingId;
    const amenitiesFilter = activeAmenitiesRef.current;
    const batchIndex = Math.floor(availableSpaces.length / SPACES_PAGE_SIZE);
    const startTime = Date.now();

    dispatch(setSpacesLoadingMore(true));
    dispatch(setSpacesBatchError(null));
    try {
      const result = await triggerGetSpaces({
        buildingId,
        ...(dateRange?.start ? { startDate: dateRange.start.slice(0, 10) } : {}),
        ...(dateRange?.end ? { endDate: dateRange.end.slice(0, 10) } : {}),
        ...(timeRange?.start ? { startTime: timeRange.start } : {}),
        ...(timeRange?.end ? { endTime: timeRange.end } : {}),
        ...(amenitiesFilter && amenitiesFilter.length > 0
          ? { amenities: amenitiesFilter.join(',') }
          : {}),
        ...(Number.isFinite(attendeeCount) && attendeeCount > 0 ? { capacity: attendeeCount } : {}),
        pageSize: SPACES_PAGE_SIZE,
        continuationToken: spacesContinuationToken,
      }).unwrap();
      dispatch(appendAvailableSpaces(result.data));
      dispatch(setSpacesContinuationToken(result.pagination?.continuationToken ?? null));
      dispatch(setSpacesHasMore(Boolean(result.pagination?.hasMore)));
      telemetry.trackEvent(TELEMETRY_COMPONENT, EVENT_TYPES.API_CALL, {
        apiEndpoint: 'GET /v1/spaces (batch)',
        batchIndex: String(batchIndex),
        durationMs: String(Date.now() - startTime),
        status: 'success',
        spacesReturned: String(result.data.length),
        hasMore: String(Boolean(result.pagination?.hasMore)),
      });
    } catch {
      dispatch(setSpacesBatchError(String(t('bookingForm.space.batchErrorLoading'))));
      telemetry.trackEvent(TELEMETRY_COMPONENT, EVENT_TYPES.API_CALL, {
        apiEndpoint: 'GET /v1/spaces (batch)',
        batchIndex: String(batchIndex),
        durationMs: String(Date.now() - startTime),
        status: 'failure',
        spacesReturned: '0',
        hasMore: String(spacesHasMore),
      });
    } finally {
      dispatch(setSpacesLoadingMore(false));
    }
  }, [
    spacesLoadingMore,
    spacesLoading,
    spacesHasMore,
    spacesContinuationToken,
    formData,
    availableSpaces.length,
    dispatch,
    triggerGetSpaces,
    t,
  ]);

  const onNext = useCallback(() => {
    void handleNext();
  }, [handleNext]);
  const onSaveAsDraft = useCallback(() => {
    void handleSaveAsDraft();
  }, [handleSaveAsDraft]);
  const onDiscard = useCallback(() => {
    void handleDiscard();
  }, [handleDiscard]);
  const onSubmit = useCallback(() => {
    void handleSubmit();
  }, [handleSubmit]);
  const onRetrySpaces = useCallback(() => {
    void handleRetrySpaces();
  }, [handleRetrySpaces]);
  const onAmenityFilterChange = useCallback(
    (amenities: string[]) => {
      void handleAmenityFilterChange(amenities);
    },
    [handleAmenityFilterChange]
  );
  const onBack = useCallback(() => {
    void handleBack();
  }, [handleBack]);

  // Disable Back on review step when resuming a draft directly,
  // unless the user has already navigated away (e.g. via Edit) and come back.
  const reviewStepIndex = dynamicConfig.steps.findIndex((s) => s.type === 'review');
  const backDisabledOnReview =
    isResuming && currentStep === reviewStepIndex && !hasNavigatedFromReviewRef.current;

  return {
    formData,
    currentStep,
    highestVisitedStep,
    selectedSpace,
    availableSpaces,
    spacesLoading,
    spacesLoadingMore,
    spacesHasMore,
    spacesBatchError,
    loadMoreSpaces,
    submissionStatus,
    apiError,
    requestId,
    validationErrors,
    dynamicConfig,
    selectedBuilding: buildingDetailData?.data ?? null,
    countriesLoading: allBuildingsLoading,
    countriesError: buildingsError,
    countriesRefetch: buildingsRefetch,
    buildingsLoading: allBuildingsLoading || allBuildingsFetching,
    buildingsError,
    buildingsRefetch,
    handleFieldChange,
    handleInlineFieldChange,
    handleUpdateFilters,
    filterFields,
    handleBack: onBack,
    handleStepClick,
    handleSelectSpace,
    handleSetupChange,
    handleBookAnother,
    handleGoHome,
    onNext,
    onSaveAsDraft,
    onDiscard,
    onSubmit,
    canSubmit,
    canSaveDraft,
    isSavingDraft,
    onRetrySpaces,
    onAmenityFilterChange,
    stepCompletionMap,
    backDisabledOnReview,
  };
}
